import { Phone, Wrench, ChevronDown, User, Building2, Star, Shield, Clock } from "lucide-react";
import heroImage from "@/assets/hero-tools.webp";

type ClientType = "individual" | "business";

interface HeroSectionProps {
  clientType: ClientType;
  onClientTypeChange: (type: ClientType) => void;
}

const HeroSection = ({ clientType, onClientTypeChange }: HeroSectionProps) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Sekcja główna">
      <img
        src={heroImage}
        alt="Narzędzia fachowca – hydraulik, elektryk, złota rączka Poznań"
        className="absolute inset-0 w-full h-full object-cover"
        width={1536}
        height={1024}
        fetchPriority="high"
        decoding="async"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 via-foreground/70 to-foreground/85" />
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 backdrop-blur-sm px-4 py-1.5 mb-8">
          <Wrench className="w-4 h-4 text-accent" />
          <span className="text-sm font-medium text-accent">Fachowiec na godziny · Poznań i okolice</span>
        </div>
        <h1 className="text-5xl md:text-7xl font-heading font-bold text-primary-foreground mb-6 leading-tight">
          Pan Fixer<br />
          <span className="text-gradient">Usterka STOP</span>
        </h1>
        <p className="text-xl md:text-2xl text-primary-foreground/80 mb-4 font-heading font-semibold">
          Serwis Dom & Firma
        </p>
        <p className="text-base md:text-lg text-primary-foreground/60 mb-8 max-w-2xl mx-auto font-body leading-relaxed">
          Hydraulik · Elektryk · AGD · TV/RTV · Złota rączka · Komputery · Tapetowanie · Usługi stolarskie · Remonty · Strony WWW
        </p>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-10 text-primary-foreground/50 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-accent fill-accent" />
            <span><strong className="text-primary-foreground/80">5.0/5</strong> · 47 opinii</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-accent" />
            <span>Gwarancja + OC</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-accent" />
            <span>Reakcja w 1–3h</span>
          </div>
        </div>

        {/* Client Type Selector */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-10">
          <button
            onClick={() => onClientTypeChange("individual")}
            aria-pressed={clientType === "individual"}
            className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg ${
              clientType === "individual"
                ? "bg-accent text-accent-foreground hover:brightness-110 scale-[1.02]"
                : "border-2 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            }`}
          >
            <User className="w-5 h-5" />
            Dla klientów indywidualnych
          </button>
          <button
            onClick={() => onClientTypeChange("business")}
            aria-pressed={clientType === "business"}
            className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg ${
              clientType === "business"
                ? "bg-accent text-accent-foreground hover:brightness-110 scale-[1.02]"
                : "border-2 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            }`}
          >
            <Building2 className="w-5 h-5" />
            Dla firm
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href="tel:+48788381508"
            className="inline-flex items-center gap-3 bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-xl text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            aria-label="Zadzwoń pod numer 788 381 508"
          >
            <Phone className="w-5 h-5" />
            788 381 508
          </a>
          <a
            href={clientType === "individual" ? "#uslugi" : "#uslugi-b2b"}
            className="inline-flex items-center gap-2 border border-primary-foreground/30 text-primary-foreground px-8 py-4 rounded-xl text-lg hover:bg-primary-foreground/10 transition-all"
          >
            {clientType === "individual" ? "Nasze usługi" : "Usługi dla firm"}
            <ChevronDown className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
        <ChevronDown className="w-6 h-6 text-primary-foreground/40" />
      </div>
    </section>
  );
};

export default HeroSection;
